import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Mail,
  ExternalLink,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { schedulingUtils, SCHEDULING_CONFIG } from '../../config/scheduling';

export interface DemoSchedulerProps {
  /** Source of the scheduling request for proper flow handling */
  source: 'header' | 'pricing-silver' | 'pricing-gold' | 'pricing-custom';
  /** Custom styling classes */
  className?: string;
  /** Callback when assessment is required */
  onAssessmentRequired?: (redirectUrl: string) => void;
  /** Callback when demo scheduling is accessed */
  onDemoAccessed?: () => void;
}

export const DemoScheduler: React.FC<DemoSchedulerProps> = ({
  source,
  className = '',
  onAssessmentRequired,
  onDemoAccessed,
}) => {
  const assessmentStatus = schedulingUtils.getAssessmentStatus(source);

  // Handle scheduling access
  const handleScheduleClick = () => {
    // Track the attempt
    schedulingUtils.trackEvent(
      assessmentStatus.canSchedule
        ? SCHEDULING_CONFIG.analytics.events.DEMO_ACCESSED
        : SCHEDULING_CONFIG.analytics.events.BLOCKED_NO_ASSESSMENT,
      { source },
    );

    if (!assessmentStatus.canSchedule) {
      // Redirect to assessment
      if (assessmentStatus.redirectUrl) {
        onAssessmentRequired?.(assessmentStatus.redirectUrl);
        window.location.href = assessmentStatus.redirectUrl;
      }
      return;
    }

    // Assessment completed - show scheduling options
    onDemoAccessed?.();
    openSchedulingOptions();
  };

  const openSchedulingOptions = () => {
    // Open Google Calendar appointment slots
    const calendarUrl = SCHEDULING_CONFIG.googleCalendar.appointmentSlotsUrl;

    // Open calendar scheduling in new window with optimal size
    window.open(
      calendarUrl,
      '_blank',
      'width=900,height=750,scrollbars=yes,resizable=yes,location=yes',
    );
  };

  if (!assessmentStatus.canSchedule) {
    return (
      <Card className={`w-full max-w-md mx-auto ${className}`}>
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
          <CardTitle className="text-xl font-bold">Assessment Required</CardTitle>
          <p className="text-gray-600 text-sm">{assessmentStatus.message}</p>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              We need to understand your community first so we can show you the most relevant
              features during your personalized demo.
            </AlertDescription>
          </Alert>

          {/* Assessment Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Personalized Demo</p>
                <p className="text-gray-600 text-sm">See features that match your specific needs</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Save Time</p>
                <p className="text-gray-600 text-sm">Get accurate pricing and timeline estimates</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Expert Preparation</p>
                <p className="text-gray-600 text-sm">
                  Our team prepares specifically for your community
                </p>
              </div>
            </div>
          </div>

          {/* Assessment CTA */}
          <Button
            onClick={handleScheduleClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            size="lg"
          >
            Complete Assessment First
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Time estimate */}
          <div className="text-center pt-2">
            <Badge variant="outline" className="text-xs">
              Takes 5-10 minutes • Progress saved automatically
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Assessment completed - show scheduling options
  const assessmentData = schedulingUtils.getCompletedAssessmentData();

  return (
    <Card className={`w-full max-w-md mx-auto ${className}`}>
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <CardTitle className="text-xl font-bold">Ready to Schedule!</CardTitle>
        <p className="text-gray-600 text-sm">
          Assessment completed for{' '}
          <strong>{(assessmentData?.communityName as string) || 'your community'}</strong>
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Community Info Summary */}
        {assessmentData && (
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Your Demo Will Cover:</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Solutions for {(assessmentData.totalUnits as string) || 'your'} units
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Focus on your priority goals
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Custom pricing recommendation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Implementation timeline for your needs
              </li>
            </ul>
          </div>
        )}

        {/* Scheduling Options */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Choose How to Schedule:</h4>

          {/* Google Calendar Scheduling */}
          <Button
            onClick={handleScheduleClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold justify-between"
            size="lg"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Schedule Demo Now
            </div>
            <ExternalLink className="w-4 h-4" />
          </Button>

          {/* Info text */}
          <div className="p-3 bg-blue-50 rounded-lg text-center">
            <p className="text-sm text-blue-600 mb-2">
              <strong>Professional Calendar Booking</strong>
            </p>
            <p className="text-xs text-blue-500">Opens Google Calendar with available time slots</p>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="border-t pt-4">
          <h4 className="font-semibold text-gray-900 mb-2">What Happens Next:</h4>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                1
              </span>
              <span>Select your preferred time slot</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                2
              </span>
              <span>Fill out booking form with email verification</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                3
              </span>
              <span>30-minute personalized demo call</span>
            </div>
          </div>
        </div>

        {/* Trust Signals */}
        <div className="text-center pt-2 border-t">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>No obligation • Cancel anytime • Free consultation</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
